import { useState } from "react";
import { Link } from "react-router-dom";
import Moment from "react-moment";
import { DatePicker, Empty, Modal, Pagination } from "antd";
import Loader from "../../../Components/Loader";
import DashboardLayout from "../../../Layout/DashboardLayout";
import { useGetAuditTrailQuery } from "../../../hooks/AuditTrail/useGetAuditTrailQuery";
import LimitSelector from "./components/LimitSelector";
import { useDebounce } from "../../../hooks/useDebounce";

interface Admin {
  firstName: string;
  lastName: string;
  id: string;
}

interface AuditLog {
  action: string;
  adminName: string;
  description: string;
  module: string;
  ip: string;
  location: string;
  device: string;
  deviceInfo: string;
  previousValue: any;
  updatedValue: any;
  admin: Admin;
  createdAt: string;
  updatedAt: string;
  id: string;
}

const AuditTrail = () => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDates, setSelectedDates] = useState<any>(null);
  const [payload, setPayload] = useState({ startDate: "", endDate: "" });
  const [search, setSearch] = useState("");
  const [activePage, setActivePage] = useState(1);
  const [limit, setLimit] = useState(10);

  const debouncedSearch = useDebounce(search, 500);

  const {
    data: auditTrail,
    isPending,
    isLoading,
    isError,
  } = useGetAuditTrailQuery({
    search: debouncedSearch,
    startDate: payload?.startDate,
    endDate: payload?.endDate,
    module: "",
    page: activePage,
    limit: limit,
  });

  const meta = auditTrail?.data?.meta;

  const [selectedAudit, setSelectedAudit] = useState<AuditLog | null>(null);

  const handlePreview = (audit: AuditLog) => {
    setSelectedAudit(audit);
    setIsEditModalOpen(true);
  };

  const { RangePicker } = DatePicker;

  const handlePageChange = (page: number) => {
    setActivePage(page);
  };

  if (isError)
    return (
      <DashboardLayout title="Audit Trail" back={false}>
        <p className="text-center">Error Occured</p>
      </DashboardLayout>
    );

  return (
    <DashboardLayout
      title="Audit Trail"
      back={false}
      right={
        <div className="flex gap-2 items-center">
          <LimitSelector limit={limit} setLimit={setLimit} />

          <input
            id=""
            type="text"
            name="search"
            placeholder="Search Admin, Action, IP, Product "
            className="p-3 bg-[#eeeeee] text-[#3d3d3d] rounded-md text-[12px] w-[250px]"
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      }
    >
      {isPending ? (
        <Loader />
      ) : (
        <div className="pl-[19px] pr-[40px] manrope">
          <div className="flex border-b-[1px] border-[#eeeeee] mb-3 justify-between items-center">
            <div className="flex justify-between items-center"></div>
            <div className="flex gap-2 justify-between items-center pr-[40px]">
              <RangePicker
                value={selectedDates}
                showTime
                onCalendarChange={(dates, dateStrings) => {
                  if (dates && dateStrings[0] && dateStrings[1]) {
                    const startDate = new Date(dateStrings[0]).toISOString();
                    const endDate = new Date(dateStrings[1]).toISOString();
                    setPayload({ startDate: startDate, endDate: endDate });
                    setSelectedDates(dates);
                  } else {
                    setSelectedDates(null);
                    setPayload({ startDate: "", endDate: "" });
                  }
                }}
              />
            </div>
          </div>
          <div className="h-[70vh] overflow-y-scroll">
            {!isPending && auditTrail && auditTrail?.data?.data.length === 0 ? (
              <div className="py-[30px]">
                <Empty />
              </div>
            ) : (
              auditTrail?.data?.data.map((data: any) => (
                <div key={data?.date} className="flex gap-6">
                  <div className="font-bold">{data?.date}</div>
                  <div className="flex-1">
                    {data?.activities.map((datum: any) => (
                      <div className="pb-5 flex gap-3 border-[#ddd] border-l-[1px]">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={1.5}
                          stroke="currentColor"
                          className={`size-6 rounded-full text-white p-1 -ml-[12px] ${
                            datum?.action.includes("Added") && "bg-orange"
                          } ${
                            datum?.action.includes("Logged") && "bg-green-600"
                          } ${
                            datum?.action.includes("Update") && "bg-[#007BFF]"
                          }`}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z"
                          />
                        </svg>

                        <div>
                          <div>
                            <Link
                              to={`/admin-management/user/${datum?.admin?.id}`}
                              className="text-orange underline underline-offset-2 font-bold"
                            >
                              {datum?.adminName}
                            </Link>{" "}
                            <span className="text-black">{datum?.action}</span>{" "}
                            at{" "}
                            <Moment
                              interval={0}
                              format="DD-MM-YYYY hh:mm a"
                              className="text-[#939393] italic"
                            >
                              {datum?.createdAt}
                            </Moment>
                            <button
                              onClick={() => handlePreview(datum)}
                              className="bg-orange text-sm text-white ml-2"
                            >
                              More
                            </button>
                          </div>
                          <p className="text-[#939393] mt-2 text-[12px]">
                            IP Address: {datum?.ip}
                          </p>
                          <p className="text-[#939393] mt-1 text-[12px]">
                            Location: {datum?.location}
                          </p>
                          <p className="text-[#939393] mt-1 text-[12px]">
                            Device Info: {datum?.deviceInfo}
                          </p>
                          <p className="text-[#939393] mt-1 text-[12px]">
                            Module: {datum?.module}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          {auditTrail && !isPending && (
            <div className="mt-2">
              <Pagination
                size="small"
                current={meta?.page}
                total={meta?.totalPages}
                onChange={handlePageChange}
              />
            </div>
          )}
          <Modal
            title="More Details"
            open={isEditModalOpen}
            onCancel={() => {
              setSelectedAudit(null);
              setIsEditModalOpen(false);
            }}
            footer={null}
          >
            <pre className="overflow-x-scroll">
              {JSON.stringify(selectedAudit, null, 2)}
            </pre>
          </Modal>
        </div>
      )}
    </DashboardLayout>
  );
};

export default AuditTrail;
